import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Input,
  Text,
  VStack,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Spinner,
  Divider,
} from "@chakra-ui/react";

const api = (
  process.env.REACT_APP_API_HOST || "https://api.balldontlie.io"
).replace(/\/$/, "");
function savedCSRF(id: string) {
  try {
    return sessionStorage.getItem(`oauth-csrf:${id}`) || "";
  } catch {
    return "";
  }
}
function storeCSRF(id: string, csrf: string) {
  try {
    for (const key of Object.keys(sessionStorage))
      if (key.startsWith("oauth-csrf:") && key !== `oauth-csrf:${id}`)
        sessionStorage.removeItem(key);
    sessionStorage.setItem(`oauth-csrf:${id}`, csrf);
  } catch {}
}
type Context = { interaction: string; csrf: string };
export default function OAuth() {
  const connections = window.location.pathname === "/oauth/connections";
  const [context, setContext] = useState<Context>({
    interaction:
      new URLSearchParams(window.location.search).get("interaction") || "",
    csrf:
      new URLSearchParams(window.location.hash.slice(1)).get("csrf") ||
      savedCSRF(
        new URLSearchParams(window.location.search).get("interaction") || ""
      ),
  });
  const [data, setData] = useState<any>(null),
    [email, setEmail] = useState(""),
    [password, setPassword] = useState("");
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [loggedIn, setLoggedIn] = useState(false);
  async function request(path: string, body?: object) {
    const r = await fetch(`${api}/oauth/${path}`, {
      credentials: "include",
      ...(body
        ? {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        : {}),
    });
    if (!r.ok) {
      if (r.status === 401) throw new Error("Invalid email or password.");
      if (r.status === 429)
        throw new Error("Too many attempts. Please try again in a minute.");
      throw new Error(
        "This request expired or could not be completed. Start again from the app you want to connect."
      );
    }
    return r.json();
  }
  useEffect(() => {
    document.title = connections
      ? "Connected apps | BALLDONTLIE"
      : "Connect to BALLDONTLIE";
    if (connections) {
      request("connections/start")
        .then((c) => {
          setContext(c);
          setData({});
        })
        .catch((e) => setError(e.message));
    } else {
      request(
        `interaction?interaction=${encodeURIComponent(context.interaction)}`
      )
        .then((d) => {
          setData(d);
          setLoggedIn(Boolean(d.email));
        })
        .catch((e) => setError(e.message));
    }
    // Keep the CSRF value out of copied URLs/history after reading it.
    if (context.csrf && context.interaction)
      storeCSRF(context.interaction, context.csrf);
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function login(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await request("login", { ...context, email, password });
      setPassword("");
      setLoggedIn(true);
      setData(
        connections
          ? await request(
              `connections?interaction=${encodeURIComponent(
                context.interaction
              )}`
            )
          : { ...data, email: user.email }
      );
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function disconnect(id: string) {
    setBusy(true);
    setError("");
    try {
      await request("connections", { ...context, grant_id: id });
      setData(
        await request(
          `connections?interaction=${encodeURIComponent(context.interaction)}`
        )
      );
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Container maxW="lg" py={{ base: 10, md: 20 }}>
      <Text
        fontWeight="bold"
        letterSpacing="0.12em"
        fontSize="sm"
        color="brand.300"
        mb={8}
      >
        BALLDONTLIE
      </Text>
      <Box
        bg="gray.800"
        border="1px solid"
        borderColor="gray.700"
        borderRadius="xl"
        p={{ base: 6, md: 8 }}
      >
        <VStack align="stretch" spacing={6}>
          <Heading size="lg">
            {connections
              ? "Connected apps"
              : loggedIn
              ? "Allow access?"
              : "Sign in to connect"}
          </Heading>
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">{error}</Text>
            </Alert>
          )}
          {!data && !error && <Spinner />}
          {data && !loggedIn && (
            <form onSubmit={login}>
              <VStack align="stretch" spacing={5}>
                <Text color="gray.300">
                  {connections ? (
                    "Sign in to manage your connected apps."
                  ) : (
                    <>
                      Continue to <strong>{data.client_name}</strong> with your
                      BALLDONTLIE account.
                    </>
                  )}
                </Text>
                <FormControl isRequired>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Password</FormLabel>
                  <Input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </FormControl>
                <Flex justify="flex-end">
                  <Button type="submit" colorScheme="blue" isLoading={busy}>
                    Sign in
                  </Button>
                </Flex>
              </VStack>
            </form>
          )}
          {data && loggedIn && !connections && (
            <>
              <Text>
                <strong>{data.client_name}</strong> wants to read sports data
                using your BALLDONTLIE account.
              </Text>
              <Box bg="gray.900" p={4} borderRadius="md">
                <Text fontSize="sm" color="gray.300">
                  Signed in as
                </Text>
                <Text wordBreak="break-word">{data.email}</Text>
              </Box>
              <Text color="gray.300">
                Your subscription determines which data this app can access.
                Requests count toward your existing rate limits.
              </Text>
              <Text fontSize="sm" color="gray.400">
                You can disconnect this app at any time in Connected apps.
              </Text>
              <Divider />
              <Text fontSize="xs" color="gray.400" wordBreak="break-all">
                Return to {data.redirect_origin}
              </Text>
              <form action={`${api}/oauth/consent`} method="post">
                <input
                  type="hidden"
                  name="interaction"
                  value={context.interaction}
                />
                <input type="hidden" name="csrf" value={context.csrf} />
                <Flex justify="flex-end" gap={3}>
                  <Button
                    name="decision"
                    value="deny"
                    type="submit"
                    variant="ghost"
                  >
                    Cancel
                  </Button>
                  <Button
                    name="decision"
                    value="allow"
                    type="submit"
                    colorScheme="blue"
                  >
                    Allow access
                  </Button>
                </Flex>
              </form>
            </>
          )}
          {data && loggedIn && connections && (
            <>
              {!data.connections?.length && (
                <Text color="gray.300">No connected apps.</Text>
              )}
              {data.connections?.map((g: any) => (
                <Box
                  key={g.id}
                  borderWidth="1px"
                  borderColor="gray.600"
                  borderRadius="md"
                  p={4}
                >
                  <Text fontWeight="bold">{g.client_name}</Text>
                  <Text
                    fontSize="xs"
                    color="gray.400"
                    wordBreak="break-all"
                    mt={1}
                  >
                    {g.client_id}
                  </Text>
                  <Flex mt={4} justify="flex-end">
                    <Button
                      size="sm"
                      isLoading={busy}
                      onClick={() => disconnect(g.id)}
                    >
                      Disconnect
                    </Button>
                  </Flex>
                </Box>
              ))}
            </>
          )}
        </VStack>
      </Box>
    </Container>
  );
}
